export function blankQuizOption() {
  return { text: '', imageUrl: '', imagePath: '', alt: '' };
}

export function blankQuizMcq() {
  return {
    question: '',
    questionImageUrl: '',
    questionImagePath: '',
    questionImageDescription: '',
    lectureId: '',
    options: [blankQuizOption(), blankQuizOption(), blankQuizOption(), blankQuizOption()],
    correct: 0,
    explanations: ['', '', '', ''],
    explanation: '',
  };
}

function parseCorrect(raw) {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    if (raw >= 0 && raw <= 3) return Math.round(raw);
    if (raw >= 1 && raw <= 4) return raw - 1;
  }
  const s = String(raw ?? '').trim();
  const letter = s.toUpperCase();
  if (letter.length === 1 && letter >= 'A' && letter <= 'D') {
    return letter.charCodeAt(0) - 65;
  }
  const n = Number(s);
  if (Number.isFinite(n)) {
    if (n >= 0 && n <= 3) return n;
    if (n >= 1 && n <= 4) return n - 1;
  }
  return 0;
}

export function normalizeQuizOption(raw) {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const text = String(raw.text || raw.label || raw.caption || '').trim();
    const imageUrl = String(raw.imageUrl || raw.url || raw.src || '').trim();
    return {
      text,
      imageUrl,
      imagePath: String(raw.imagePath || raw.path || ''),
      alt: String(raw.alt || raw.caption || text),
    };
  }
  return {
    text: String(raw || '').trim(),
    imageUrl: '',
    imagePath: '',
    alt: '',
  };
}

export function optionIsComplete(opt) {
  const n = normalizeQuizOption(opt);
  return Boolean(n.text || n.imageUrl);
}

export function optionText(opt) {
  const n = normalizeQuizOption(opt);
  return n.text || n.alt || '';
}

export function optionImageUrl(opt) {
  return normalizeQuizOption(opt).imageUrl;
}

function normalizeQuizExhibit(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((block) => {
    if (block?.type === 'table') {
      const rows = (block.rows || []).map((row) => ({
        cells: (Array.isArray(row) ? row : row.cells || []).map((cell) => String(cell || '')),
      })).filter((row) => row.cells.length);
      return rows.length ? { type: 'table', rows } : null;
    }
    if (block?.type === 'image' && block.url) {
      return { type: 'image', url: String(block.url), path: String(block.path || '') };
    }
    const text = String(block?.text || '').trim();
    if (block?.type === 'text' && text) return { type: 'text', text };
    return null;
  }).filter(Boolean);
}

export function normalizeAuthoredQuestions(questions = []) {
  return (questions || [])
    .map((q) => {
      const options = [0, 1, 2, 3].map((i) => normalizeQuizOption(q?.options?.[i]));
      const correct = parseCorrect(q?.correct);
      const fromArray = Array.isArray(q?.explanations)
        ? [0, 1, 2, 3].map((i) => String(q.explanations[i] || '').trim())
        : ['', '', '', ''];
      const single = String(q?.explanation || '').trim();
      const explanations = fromArray.map((text, i) => {
        if (text) return text;
        if (single && i === correct) return single;
        return '';
      });
      return {
        question: String(q?.question || q?.prompt || q?.stem || '').trim(),
        questionImageUrl: String(q?.questionImageUrl || q?.imageUrl || '').trim(),
        questionImagePath: String(q?.questionImagePath || ''),
        questionImageDescription: String(q?.questionImageDescription || q?.imageDescription || '').trim(),
        lectureId: String(q?.lectureId || ''),
        passage: String(q?.passage || '').trim(),
        exhibit: normalizeQuizExhibit(q?.exhibit),
        options,
        correct,
        explanations,
        explanation: explanations[correct] || single,
      };
    })
    .filter((q) => q.question && q.options.every(optionIsComplete));
}

export function editorQuestionsFrom(raw) {
  const normalized = normalizeAuthoredQuestions(raw);
  if (normalized.length) {
    return normalized.map((q) => ({
      ...q,
      options: q.options.map((o) => ({ ...o })),
      explanations: [...(q.explanations || ['', '', '', ''])],
    }));
  }
  return [blankQuizMcq()];
}

export function persistableQuizQuestions(questions = []) {
  return normalizeAuthoredQuestions(questions).map((q) => ({
    question: q.question,
    questionImageUrl: q.questionImageUrl || '',
    questionImagePath: q.questionImagePath || '',
    questionImageDescription: q.questionImageDescription || '',
    lectureId: q.lectureId || '',
    passage: q.passage || '',
    exhibit: q.exhibit || [],
    options: q.options.map((o) => ({
      text: o.text || '',
      imageUrl: o.imageUrl || '',
      imagePath: o.imagePath || '',
      alt: o.alt || '',
    })),
    correct: q.correct,
    explanations: q.explanations,
    explanation: q.explanation || q.explanations[q.correct] || '',
  }));
}

/** Shape QuizModal expects. */
export function toQuizModalQuestions(questions = []) {
  return persistableQuizQuestions(questions);
}

export const LECTURE_QUIZ_DRAW = 5;
export const MODULE_EXAM_DRAW = 25;

function questionKey(q) {
  const opts = (q?.options || []).map((o) => `${optionText(o)}|${optionImageUrl(o)}`).join('~');
  return `${String(q?.question || '').trim().toLowerCase()}|${q?.questionImageUrl || ''}|${opts}`;
}

export function shuffleQuizQuestions(questions = []) {
  const copy = [...(questions || [])];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Pick up to 5 lecture questions. On retry, prefer items not in the previous draw. */
export function drawLectureQuizQuestions(pool, { previousKeys = [], count = LECTURE_QUIZ_DRAW } = {}) {
  const all = toQuizModalQuestions(pool);
  if (!all.length) return [];
  const n = Math.min(count, all.length);
  const prev = new Set((previousKeys || []).filter(Boolean));
  const unused = all.filter((q) => !prev.has(questionKey(q)));
  const source = unused.length >= n ? unused : all;
  return shuffleQuizQuestions(source).slice(0, n);
}

export function lectureQuizQuestionKeys(questions = []) {
  return (questions || []).map(questionKey);
}

function takeBalanced(items, n, prev, usedKeys) {
  if (n <= 0 || !items?.length) return [];
  const available = items.filter((q) => !usedKeys.has(questionKey(q)));
  const unused = available.filter((q) => !prev.has(questionKey(q)));
  const ordered = [...shuffleQuizQuestions(unused), ...shuffleQuizQuestions(available.filter((q) => prev.has(questionKey(q))))];
  const take = ordered.slice(0, n);
  take.forEach((q) => usedKeys.add(questionKey(q)));
  return take;
}

/**
 * 25-question module exam: equal count from each content lecture's quiz bank,
 * then fill from other lectures and untagged module extras.
 */
export function drawModuleExamQuestions({
  days = [],
  moduleQuestions = [],
  previousKeys = [],
  count = MODULE_EXAM_DRAW,
} = {}) {
  const prev = new Set((previousKeys || []).filter(Boolean));
  const usedKeys = new Set();
  const extras = toQuizModalQuestions(moduleQuestions);
  const contentDays = (days || []).filter((d) => !d.isRevisionLecture);
  const buckets = contentDays.map((day) => {
    const fromLecture = toQuizModalQuestions(day.quizQuestions);
    const tagged = extras.filter((q) => q.lectureId && q.lectureId === day.id);
    return [...fromLecture, ...tagged];
  });
  const untagged = extras.filter((q) => !q.lectureId);

  if (!buckets.length) {
    return takeBalanced(untagged, count, prev, usedKeys);
  }

  const nBuckets = buckets.length;
  const base = Math.floor(count / nBuckets);
  const remainder = count % nBuckets;
  const drawn = [];

  buckets.forEach((items, i) => {
    const share = base + (i < remainder ? 1 : 0);
    drawn.push(...takeBalanced(items, share, prev, usedKeys));
  });

  let needed = count - drawn.length;
  if (needed > 0) {
    const leftover = buckets.flat();
    drawn.push(...takeBalanced(leftover, needed, prev, usedKeys));
    needed = count - drawn.length;
  }
  if (needed > 0) {
    drawn.push(...takeBalanced(untagged, needed, prev, usedKeys));
  }

  return shuffleQuizQuestions(drawn).slice(0, count);
}

export function importQuizJson(raw) {
  let data = raw;
  if (typeof raw === 'string') {
    data = JSON.parse(raw);
  }
  if (data == null) throw new Error('JSON file is empty.');
  let list = [];
  if (Array.isArray(data)) list = data;
  else if (Array.isArray(data.questions)) list = data.questions;
  else if (Array.isArray(data.quiz)) list = data.quiz;
  else if (Array.isArray(data.mcqs)) list = data.mcqs;
  else {
    throw new Error(
      'Unrecognized quiz JSON. Use { "questions": [{ "question", "options": ["A","B","C","D"], "correct": 0 }] }.'
    );
  }
  const questions = normalizeAuthoredQuestions(list);
  if (!questions.length) {
    throw new Error('No complete MCQs found. Each question needs written text and four options (text and/or an image).');
  }
  return {
    title: data && !Array.isArray(data) ? String(data.title || '') : '',
    questions: editorQuestionsFrom(questions),
  };
}

export const SAMPLE_QUIZ_JSON = {
  _chatgpt:
    'Write GED-level multiple choice. Each question needs written text and exactly 4 options. correct is 0-based (0 = A). Options may be strings or { "text", "imageUrl" }. Attach local images in the admin editor after import.',
  title: 'Sample quiz',
  questions: [
    {
      question: 'When two fractions have the same denominator, how do you add them?',
      options: [
        'Add the denominators and keep the numerators',
        'Add the numerators and keep the same denominator',
        'Multiply the numerators and denominators',
        'Always convert to decimals first',
      ],
      correct: 1,
      explanations: [
        'A: The denominator stays the same when it is already shared.',
        'B: Correct — add the top numbers and keep the bottom number.',
        'C: That would multiply, not add.',
        'D: Converting to decimals is not required for like denominators.',
      ],
    },
    {
      question: 'What is 1/5 + 2/5?',
      options: ['2/10', '3/10', '3/5', '1/5'],
      correct: 2,
      explanations: [
        'A: Adding denominators is incorrect.',
        'B: The denominator should stay 5.',
        'C: Correct — 1 + 2 = 3 over the same denominator 5.',
        'D: That is only the first addend, not the sum.',
      ],
    },
  ],
};

export function sampleQuizJsonText() {
  return `${JSON.stringify(SAMPLE_QUIZ_JSON, null, 2)}\n`;
}

export async function quizQuestionsFromDocx(parsed, uploadImage) {
  const uploaded = {};
  const entries = Object.entries(parsed?.media || {});
  for (let index = 0; index < entries.length; index += 1) {
    const [id, file] = entries[index];
    const imageFile = new File(
      [file.bytes],
      file.name || 'image.png',
      { type: file.contentType || 'image/png' },
    );
    uploaded[id] = await uploadImage(imageFile);
  }
  const questions = (parsed?.questions || []).map((question) => {
    const exhibit = normalizeQuizExhibit((question.exhibit || []).map((block) => {
      if (block?.type !== 'image') return block;
      const file = uploaded[block.mediaId];
      if (!file?.url) return null;
      return { type: 'image', url: file.url, path: file.path || '' };
    }).filter(Boolean));
    const firstImage = exhibit.find((block) => block.type === 'image');
    const passage = exhibit.filter((block) => block.type === 'text').map((block) => block.text).join('\n\n');
    return {
      question: question.stem,
      questionImageUrl: firstImage?.url || '',
      questionImagePath: firstImage?.path || '',
      passage,
      exhibit,
      options: (question.options || []).slice(0, 4).map((option) => {
        const file = uploaded[option.mediaIds?.[0]];
        return {
          text: option.text || '',
          imageUrl: file?.url || '',
          imagePath: file?.path || '',
          alt: option.text || '',
        };
      }),
      correct: question.correct,
      explanation: question.explanation || '',
    };
  });
  const imported = editorQuestionsFrom(questions);
  const complete = imported.filter((question) => question.question && question.options.every(optionIsComplete));
  if (!complete.length || complete.length !== questions.filter((question) => question.question).length) {
    if (!complete.length) {
      throw new Error('No complete questions were found. Each question needs a stem and four options.');
    }
  }
  if (!complete.length) throw new Error('No complete questions were found in that document.');
  return complete;
}
